import { useMemo, useState, useContext } from 'react'
import { MRT_ColumnDef, MRT_SortingState } from 'material-react-table'
import Title from '../../components/common/title'
import useSelectedItemSideBar from '../../hooks/useSelectedItemSideBar'
import CustomTable from '../../components/common/customTable'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useFormik } from 'formik'
import FormikContext from '../../context/formikContext'
import FieldWithLabel from '../../components/common/fields/fieldWithLabel'
import OutlinedFieldFormik from '../../components/formik/fields/outlinedFieldFormik'
import ExternalLinkButton from '../../components/common/buttons/externalLinkButton'
import ContainedButton from '../../components/common/buttons/containedButton'
import ResetButton from '../../components/common/buttons/resetButton'
import { ActiveEnterpriseContext } from '../../context/activeEnterpriseContext'
import { OrderType } from '../../types/api/entreprise'
import {  useGetOrdersQuery } from '../../hooks/api/entreprises'
import { FilterType } from '../../types/api'
import {getOrderType} from '../../utils'
import Pagination from '../../components/common/pagination'


const NumOrderTitle = () => <Box sx={{ pl: '15px', whiteSpace: 'wrap', pr: '5px'  }}>Numéro de facture</Box>
const NumOrderBody = ({ row }: any) => (
  <Typography variant='bodySmall' sx={{ pl: '15px'}}>
    {row.original.order_number}
  </Typography>
)
const DateBody = ({ row }: any) => (
  <Typography variant='bodySmall'>
    {row.original.date_entered}
  </Typography>
)
const AcqBody = ({ row }: any ) => (
  <Typography variant='bodySmall'>
     {row.original.region_name}
  </Typography>
)
const TotalBody = ({ row }: any ) => (
  <Typography variant='bodySmall'>
     {row.original.total} $
  </Typography>
)
const SoldBody = ({ row }: any ) => (
  <Typography variant='bodySmall'>
     {row.original.credit} $
  </Typography>
)
const StatusBody = ({ row }: any ) => (
  <Typography variant='bodySmall'>
    {getOrderType(row.original.status)}
  </Typography>
)
const RowActions = ({ row }: any) => (
  <Box sx={{ pr: '10px'}}>
  <ExternalLinkButton href={`/commande/${row.original.crm_id}`}/>
  </Box>
)
const DetailsColumn = () => <Box sx={{ pr: '24px' }}>Détail</Box>


const MyOrders = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery('(max-width:900px)') // 900px breakpoint
  const [filter, setFilter] = useState<FilterType>({})
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'date_entered', desc: true }])
  const [page, setPage] = useState('1')
  const { data, isFetching } = useGetOrdersQuery({
    page,
    sort: sorting[0],
    filter,
  })
  useSelectedItemSideBar('myProfile.orders')
  const columns = useMemo<MRT_ColumnDef<OrderType>[]>(
    () => [
      {
        accessorKey: 'order_number',
        header: 'Numéro de facture',
        Header: NumOrderTitle,
        maxSize: 29,
        minSize: 29,
        size: 29,
        enableSorting: true,
        Cell: NumOrderBody,
      },
      {
        accessorKey: 'date_entered',
        header: 'Date',
        maxSize: 29,
        minSize: 29,
        size: 29,
        enableSorting: true,
        Cell: DateBody,
      },
      {
        accessorKey: 'region_name',
        header: 'ACQ',
        size: 29,
        enableSorting: true,
        maxSize: 29,
        minSize: 29,
        Cell: AcqBody,
      },
      {
        accessorKey: 'total',
        header: 'Total',
        size: 29,
        enableSorting: true,
        maxSize: 29,
        minSize: 29,
        Cell: TotalBody,
      },
      {
        accessorKey: 'credit',
        header: 'Solde',
        size: 29,
        enableSorting: true,
        maxSize: 29,
        minSize: 29,
        Cell: SoldBody,
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        size: 29,
        maxSize: 29,
        minSize: 29,
        Cell: StatusBody,
        enableSorting: true,
      },
    ],
    [],
  )
  const formik = useFormik({
    initialValues: {
      search: '',
    },
    onSubmit: (values) => {
      setPage('1')
      setFilter(values)
    },
  })

  return (
    <>
      <Title sx={{ mb: '48px' }}>Mes commandes</Title>
      <Box
        sx={{
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: isMobile ? 'center' : 'space-between',
          mb: '48px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '0'
        }}
      >
        <Box sx={{
          display: 'flex',
          gap: isMobile ? '16px' : '24px',
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto'
        }}>
          <FormikContext.Provider value={formik}>
            <FieldWithLabel label='Rechercher'>
              <OutlinedFieldFormik
                sx={{
                  width: isMobile ? '100%' : '416px',
                  mt: '-12px'
                }}
                name='search'
                placeholder='Numéro, date'
              />
            </FieldWithLabel>

            <ContainedButton
              sx={{
                alignSelf: 'end',
                width: isMobile ? '100%' : '140px',
                minHeight: '44px',
                height: '44px',
                py: '6px',
                fontSize: '12px',
              }}
              title='rechercher'
              handleClick={() => formik.handleSubmit()}
            />
          </FormikContext.Provider>
        </Box>
        {formik.isSubmitting && formik.dirty && (
          <ResetButton handleClick={() => {
            setPage('1')
            setFilter({})
            formik.resetForm()
          }} />
        )}
      </Box>
      <Typography sx={{ fontWeight: '500', mb: '15px!important' }}>Résultats : {data?.metadata?.total ? data.metadata.total : 0}</Typography>

      {/* Table container with horizontal scroll on mobile */}
      <Box sx={{
        width: '100%',
        overflowX: isMobile ? 'auto' : 'visible',
        minWidth: isMobile ? '100%' : 'auto',
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#a8a8a8',
          },
        },
      }}>
        <Box sx={{
          minWidth: isMobile ? '800px' : 'auto', // Ensure table doesn't shrink below this width
          width: isMobile ? 'max-content' : '100%'
        }}>
          <CustomTable
            data={data?.data ?? []}
            columns={columns}
            enableSorting={true}
            muiTableBodyRowProps={{ hover: false }}
            displayColumnDefOptions={{
              'mrt-row-actions': {
                size: 10,
                maxSize: 10,
                minSize: 10,
                header: 'Details',
                accessorKey: 'details',
                Header: DetailsColumn,
                muiTableBodyCellProps: {
                  align: 'right',
                },
                muiTableHeadCellProps: {
                  align: 'right',
                },
              },
            }}
            enableSortingRemoval={false}
            enableMultiSort={false}
            manualSorting
            onSortingChange={(params) => {
              setPage('1')
              setSorting(params)
            }}
            enableRowActions
            state={{ sorting, isLoading: isFetching }}
            renderRowActions={RowActions}
            positionActionsColumn='last'
            enableSelectAll={false}
            muiTableBodyCellProps= {{
              sx: {
                p: '10px 7px 10px 0',
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                color: 'white',
                textTransform: 'uppercase',
                fontSize: '14px',
                lineHeight: '18px',
                fontWeight: '500',
                letterSpacing: '1px',
                justifyContent:'center',
                wordBreak: 'break-word',
                py: '11px',
                px: '0',
                pr: '7px',
                '& .MuiTableSortLabel-root': {
                  ml: '14px',
                  width: 'auto',
                },
                '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon, & .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon':
                  {
                    color: '#A5A3A3',
                  },
              },
            }}
          />
        </Box>
      </Box>

      {/* Pagination stays outside the scrollable container and centered */}
      {Number(data?.metadata?.total) / 10 > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '24px' }}>
          <Pagination
            page={Number(page)}
            count={data?.metadata?.total && (Number(data.metadata.total) > 0) ? Math.ceil(Number(data.metadata.total) / 10) : 1}
            size={isMobile ? 'small' : 'large'}
            onChange={(e, index) => {
              setPage(index.toString())
            }}
          />
        </Box>
      )}
    </>
  )
}

export default MyOrders
